import React from 'react';
import {
  ArrowBendUpRight, ArrowClockwise, ArrowSquareOut, CaretRight, CircleNotch, DownloadSimple, Folder,
  PencilSimple, Trash, WarningCircle,
} from '@phosphor-icons/react';
import { ImmoButton } from '../../../../components/immobilien/ui';
import { ORDNER_MIME, formatiereGroesse, istGoogleFormat, type DriveEintrag } from '../../../../lib/dokumente/aktenschrankApi';
import { ordnerZeile, quelleLabel, trenneEndung, type Segment } from '../../../../lib/dokumente/aktenschrank';
import type { Ansicht, DateiMeta } from '../../../../lib/dokumente/useAktenschrank';
import { dateiDarstellung, deDatumZeit, driveOrdnerUrl, ordnerFarbe } from './dateiDarstellung';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import { freigabeKurz, freigabeTip, type Freigabe } from '../../../../lib/dokumente/freigaben';

/** #607 F3: Mehrfachauswahl und Freigabe-Abzeichen. freigaben(e) = null → Datei ist nicht in der App erfasst. */
export interface Markierung {
  an: ReadonlySet<string>;
  umschalten: (e: DriveEintrag) => void;
  freigaben: (e: DriveEintrag) => readonly Freigabe[] | null;
}

/** Abzeichen „Mieter: alle“ / „Eigentümer: 2“ – nichts, wenn nicht freigegeben. */
export function FreigabeAbzeichen({ freigaben }: { freigaben: readonly Freigabe[] | null }) {
  if (!freigaben?.length) return null;
  const teile = [freigabeKurz(freigaben, 'mieter'), freigabeKurz(freigaben, 'eigentuemer')].filter(Boolean) as string[];
  return (
    <span className="flex items-center shrink-0" style={{ gap: 4 }} data-tip={`Im Portal freigegeben – ${freigabeTip(freigaben)}`}>
      {teile.map(t => (
        <span key={t} className="immo-zahl" style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--acc-soft)', color: 'var(--acc)', whiteSpace: 'nowrap' }}>{t}</span>
      ))}
    </span>
  );
}

export interface UnterordnerInfo { dateien: number | null; ordner: number | null; pfad: string }

export interface DateiAktionen {
  ausgewaehltId: string | null;
  umbenennenId: string | null;
  umbenennenWert: string;
  papierkorbId: string | null;
  laufende: ReadonlySet<string>;
  /** Mobil: Aktionen nur an der gewählten Zeile (Platz), Desktop immer. */
  aktionenNurAusgewaehlt: boolean;
  onWahl: (d: DriveEintrag) => void;
  onHerunterladen: (d: DriveEintrag) => void;
  onUmbenennenStart: (d: DriveEintrag) => void;
  onUmbenennenWert: (v: string) => void;
  onUmbenennenOk: (d: DriveEintrag) => void;
  onUmbenennenAbbruch: () => void;
  onVerschieben: (d: DriveEintrag) => void;
  onPapierkorb: (d: DriveEintrag) => void;
}

const RUND: React.CSSProperties = { borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink2)' };

function AktionKnopf({ icon: IconKomp, label, onClick, disabled, groesse }: {
  icon: typeof Trash; label: string; onClick: () => void; disabled?: boolean; groesse: number;
}) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={e => { e.stopPropagation(); onClick(); }}
      className="immo-hover-flaeche flex items-center justify-center cursor-pointer shrink-0" style={{ ...RUND, width: groesse, height: groesse, opacity: disabled ? 0.4 : 1 }}>
      <IconKomp weight="bold" size={16} />
    </button>
  );
}

/** Breadcrumb, Zähler und „In Drive öffnen“ (Handoff Dokumente §6.2, Screens 03–05). */
export function InhaltKopf({ segmente, zaehlerText, ordnerId, onSegment, hoehe = 34 }: {
  segmente: readonly Segment[]; zaehlerText: string; ordnerId: string; onSegment: (s: Segment) => void; hoehe?: number;
}) {
  return (
    <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
      <nav aria-label="Pfad" className="flex items-center flex-wrap min-w-0" style={{ gap: 2, flex: '1 1 260px' }}>
        {segmente.map((s, i) => {
          const letzte = i === segmente.length - 1;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <CaretRight weight="bold" size={11} color="var(--ink3)" aria-hidden style={{ flexShrink: 0 }} />}
              <button type="button" onClick={() => onSegment(s)} aria-current={letzte ? 'page' : undefined}
                className="immo-hover-flaeche cursor-pointer truncate" style={{ maxWidth: 220, height: hoehe, padding: '0 10px', borderRadius: 12, border: 'none', fontSize: 13,
                  fontWeight: letzte ? 800 : 600, background: letzte ? 'var(--fill2)' : 'transparent', color: letzte ? 'var(--ink)' : 'var(--ink2)' }}>
                {s.name}
              </button>
            </React.Fragment>
          );
        })}
      </nav>
      <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{zaehlerText}</span>
      <a href={driveOrdnerUrl(ordnerId)} target="_blank" rel="noreferrer" aria-label="In Drive öffnen" title="In Drive öffnen"
        className="immo-hover-flaeche flex items-center justify-center shrink-0" style={{ width: Math.max(40, hoehe), height: Math.max(40, hoehe), borderRadius: 13, background: 'var(--fill2)', color: 'var(--ink)' }}>
        <ArrowSquareOut weight="bold" size={16} />
      </a>
    </div>
  );
}

/** Unterordner und Dateien als Liste oder Kacheln. */
export function OrdnerInhalt({ eintraege, ansicht, laedt, dateienLadenNoch, fehler, onNeu, unterordner, metaNachDatei, ordnerName, aktionen, onOrdner, knopfGroesse, leer, markierung }: {
  eintraege: readonly DriveEintrag[];
  ansicht: Ansicht;
  laedt: boolean;
  dateienLadenNoch: boolean;
  fehler: string | null;
  onNeu: () => void;
  unterordner: (e: DriveEintrag) => UnterordnerInfo;
  metaNachDatei: ReadonlyMap<string, DateiMeta>;
  ordnerName: string;
  aktionen: DateiAktionen;
  onOrdner: (e: DriveEintrag) => void;
  knopfGroesse: number;
  leer: React.ReactNode;
  markierung?: Markierung;
}) {
  if (laedt) return <div className="flex items-center justify-center" style={{ padding: 48 }}><CircleNotch size={24} color="var(--ink3)" className="animate-spin" aria-label="Ordner lädt" /></div>;
  if (fehler && eintraege.length === 0) {
    return (
      <div className="flex flex-col items-center text-center" style={{ padding: '36px 16px', gap: 8 }}>
        <WarningCircle weight="fill" size={28} color="var(--bad)" />
        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{fehler}</span>
        <ImmoButton icon={ArrowClockwise} onClick={onNeu}>Erneut versuchen</ImmoButton>
      </div>
    );
  }
  if (eintraege.length === 0) return <>{leer}</>;

  const ordner = eintraege.filter(e => e.mimeType === ORDNER_MIME);
  const dateien = eintraege.filter(e => e.mimeType !== ORDNER_MIME);
  const kacheln = ansicht === 'kacheln';

  const ordnerKnoten = (e: DriveEintrag) => {
    const info = unterordner(e);
    const f = ordnerFarbe(info.pfad || e.name);
    const sub = ordnerZeile(info.dateien, info.ordner);
    if (kacheln) {
      return (
        <button key={e.id} type="button" onClick={() => onOrdner(e)} className="immo-innen immo-hover-widget flex flex-col text-left cursor-pointer min-w-0"
          style={{ borderRadius: 20, padding: 14, gap: 10, border: 'none', color: 'var(--ink)' }}>
          <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: f.bg }}><Folder weight="fill" size={22} color={f.fg} /></span>
          <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{e.name}</span>
            <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
          </span>
        </button>
      );
    }
    return (
      <button key={e.id} type="button" onClick={() => onOrdner(e)} className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer min-w-0"
        style={{ gap: 12, minHeight: 56, padding: '8px 10px', borderRadius: 16, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: f.bg }}><Folder weight="fill" size={19} color={f.fg} /></span>
        <span className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
          <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{e.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
        </span>
        <CaretRight weight="bold" size={13} color="var(--ink3)" style={{ flexShrink: 0 }} />
      </button>
    );
  };

  const dateiKnoten = (e: DriveEintrag) => {
    const a = aktionen;
    const d = dateiDarstellung(e.name, e.mimeType);
    const m = metaNachDatei.get(e.id) ?? null;
    const gewaehlt = a.ausgewaehltId === e.id;
    const bearbeitet = a.umbenennenId === e.id;
    const laeuft = a.laufende.has(e.id);
    const scharf = a.papierkorbId === e.id;
    const google = istGoogleFormat(e.mimeType);
    const sub = [formatiereGroesse(e.size), m?.kategorie, quelleLabel(m?.quelle ?? null, m?.kategorie), deDatumZeit(e.modifiedTime)].filter(Boolean).join(' · ');
    const zeigeAktionen = !kacheln && !bearbeitet && (!a.aktionenNurAusgewaehlt || gewaehlt);
    const fr = markierung ? markierung.freigaben(e) : null;
    const markiert = !!markierung?.an.has(e.id);
    const kaestchen = markierung && (
      <AuswahlKaestchen an={markiert} label={`${e.name} auswählen`} touch={knopfGroesse >= 44} gesperrt={fr === null}
        tip={fr === null ? 'Nicht in der App erfasst – erst nach dem Abgleich freigebbar' : undefined}
        onUmschalten={() => markierung.umschalten(e)} />
    );

    if (kacheln) {
      return (
        // div statt button: das Auswahlkästchen ist selbst ein Knopf (keine verschachtelten Knöpfe)
        <div key={e.id} role="button" tabIndex={0} onClick={() => a.onWahl(e)} aria-pressed={gewaehlt}
          onKeyDown={ev => { if ((ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); a.onWahl(e); } }}
          className="immo-innen immo-hover-widget flex flex-col text-left cursor-pointer min-w-0"
          style={{ borderRadius: 20, padding: 14, gap: 10, border: 'none', color: 'var(--ink)', boxShadow: gewaehlt ? 'inset 0 0 0 2px var(--acc)' : undefined }}>
          <span className="flex items-start justify-between" style={{ gap: 6 }}>
            <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: d.bg }}><d.icon weight="fill" size={22} color={d.fg} /></span>
            {kaestchen}
          </span>
          <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 800, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.name}</span>
            <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{sub}</span>
            <FreigabeAbzeichen freigaben={fr} />
          </span>
        </div>
      );
    }

    const { endung } = trenneEndung(e.name);
    return (
      <div key={e.id} role="button" tabIndex={0} aria-pressed={gewaehlt} onClick={() => !bearbeitet && a.onWahl(e)}
        onKeyDown={ev => { if (!bearbeitet && (ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); a.onWahl(e); } }}
        className="immo-hover-flaeche flex items-center w-full cursor-pointer min-w-0"
        style={{ gap: 12, minHeight: 60, flexWrap: a.aktionenNurAusgewaehlt ? 'wrap' : undefined, padding: '8px 8px 8px 10px', borderRadius: 16, color: 'var(--ink)', boxShadow: gewaehlt ? 'inset 0 0 0 2px var(--acc)' : undefined, background: gewaehlt ? 'var(--fill)' : 'transparent' }}>
        {kaestchen}
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: d.bg }}><d.icon weight="fill" size={19} color={d.fg} /></span>
        {bearbeitet ? (
          <span className="flex items-center flex-1 min-w-0" style={{ gap: 6 }} onClick={ev => ev.stopPropagation()}>
            <input autoFocus aria-label="Neuer Dateiname" value={a.umbenennenWert} onChange={ev => a.onUmbenennenWert(ev.target.value)} disabled={laeuft}
              onFocus={ev => ev.target.select()}
              onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); a.onUmbenennenOk(e); } else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); a.onUmbenennenAbbruch(); } }}
              onBlur={() => { if (!laeuft) a.onUmbenennenAbbruch(); }}
              className="flex-1 min-w-0" style={{ height: 40, padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none', fontSize: 14, fontWeight: 700, background: 'var(--bg)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 2px var(--acc)' }} />
            {endung && <span className="immo-zahl shrink-0" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>{endung}</span>}
          </span>
        ) : (
          <span className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }} title={e.name}>{e.name}</span>
            <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
          </span>
        )}
        {!bearbeitet && <FreigabeAbzeichen freigaben={fr} />}
        {laeuft && <CircleNotch size={16} color="var(--ink3)" className="animate-spin shrink-0" aria-label="Aktion läuft" />}
        {zeigeAktionen && (
          <span className="flex items-center shrink-0" style={{ gap: 2, ...(a.aktionenNurAusgewaehlt ? { flexBasis: '100%', justifyContent: 'flex-end' } : {}) }}>
            {!google && <AktionKnopf icon={DownloadSimple} label="Herunterladen" onClick={() => a.onHerunterladen(e)} disabled={laeuft} groesse={knopfGroesse} />}
            <AktionKnopf icon={PencilSimple} label="Umbenennen" onClick={() => a.onUmbenennenStart(e)} disabled={laeuft} groesse={knopfGroesse} />
            <AktionKnopf icon={ArrowBendUpRight} label="Verschieben" onClick={() => a.onVerschieben(e)} disabled={laeuft} groesse={knopfGroesse} />
            <button type="button" aria-label={scharf ? 'Wirklich in den Papierkorb?' : 'In den Papierkorb'} title={scharf ? 'Wirklich in den Papierkorb?' : 'In den Papierkorb'}
              disabled={laeuft} onClick={ev => { ev.stopPropagation(); a.onPapierkorb(e); }}
              className={`${scharf ? '' : 'immo-hover-flaeche '}flex items-center justify-center cursor-pointer shrink-0`}
              style={{ ...RUND, height: knopfGroesse, minWidth: knopfGroesse, padding: scharf ? '0 10px' : 0, gap: 5, fontSize: 12, fontWeight: 800,
                background: scharf ? 'var(--bad)' : 'transparent', color: scharf ? '#fff' : 'var(--ink2)', opacity: laeuft ? 0.4 : 1 }}>
              <Trash weight="bold" size={16} />{scharf && 'Wirklich?'}
            </button>
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: kacheln ? 12 : 2 }} aria-label={`Inhalt von ${ordnerName}`}>
      {kacheln ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 170px), 1fr))', gap: 10 }}>
          {ordner.map(ordnerKnoten)}{dateien.map(dateiKnoten)}
        </div>
      ) : (
        <>{ordner.map(ordnerKnoten)}{dateien.map(dateiKnoten)}</>
      )}
      {dateienLadenNoch && (
        <span className="flex items-center" style={{ gap: 6, padding: '8px 10px', fontSize: 12, color: 'var(--ink3)' }}>
          <CircleNotch size={13} className="animate-spin" aria-hidden />Dateien werden geladen …
        </span>
      )}
      {fehler && eintraege.length > 0 && (
        <span className="flex items-center" style={{ gap: 6, padding: '8px 10px', fontSize: 12, color: 'var(--bad)' }}>
          <WarningCircle weight="fill" size={13} />{fehler}
          <button type="button" onClick={onNeu} className="cursor-pointer" style={{ border: 'none', background: 'none', color: 'var(--acc)', fontWeight: 800, fontSize: 12 }}>Erneut versuchen</button>
        </span>
      )}
    </div>
  );
}

